/**
 * Management (Product Manager) API client.
 * Uses MANAGEMENT_API_BASE_URL from appsettings.json.
 */

import { getAppSettings } from './appSettings';

function normalizeBaseUrl(url) {
  const s = String(url || '').trim();
  if (!s) return '';
  return s.endsWith('/') ? s : `${s}/`;
}

export function getManagementApiBaseUrl() {
  const settings = getAppSettings();
  return normalizeBaseUrl(
    settings.MANAGEMENT_API_BASE_URL || settings.MANAGEMENT_API_URL || '',
  );
}

export const SUPPORT_MOBILE = '+918891426801';
export const SUPPORT_WHATSAPP = '918891426801';

export function buildWhatsAppUrl({ customerName, registerCode, reason, amount, details }) {
  const lines = [
    'Hello Ainsoft Support,',
    '',
    reason || 'I need help with my software license / payment.',
  ];
  if (customerName) lines.push(`Customer: ${customerName}`);
  if (registerCode) lines.push(`Register code: ${registerCode}`);
  if (amount != null && amount !== '') {
    lines.push(`Overdue amount: ${Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
  }
  if (details) lines.push(details);
  lines.push('', 'Please assist. Thank you.');
  return `https://wa.me/${SUPPORT_WHATSAPP}?text=${encodeURIComponent(lines.join('\n'))}`;
}

export function telSupportHref() {
  return `tel:${SUPPORT_MOBILE}`;
}

/**
 * Fetch full customer portal snapshot by RegisterCode (Customer.Code).
 * @param {string} registerCode
 */
export async function fetchCustomerPortal(registerCode) {
  const base = getManagementApiBaseUrl();
  if (!base) {
    throw new Error('MANAGEMENT_API_BASE_URL is not configured in appsettings.json');
  }
  const code = String(registerCode || '').trim();
  if (!code) {
    throw new Error('Register code is missing');
  }

  const response = await fetch(`${base}CustomerPortal/${encodeURIComponent(code)}`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    throw new Error(`Management API error (${response.status})`);
  }

  const raw = await response.json();
  return normalizePortal(raw);
}

/** Download URL for a contract file (secured by register code + contract id). */
export function getContractDownloadUrl(registerCode, contractId) {
  const base = getManagementApiBaseUrl();
  const code = String(registerCode || '').trim();
  if (!base || !code || !contractId) return '';
  return `${base}DownloadContractFile/${encodeURIComponent(code)}/${contractId}`;
}

function pick(obj, ...keys) {
  for (const key of keys) {
    if (obj && obj[key] !== undefined && obj[key] !== null) return obj[key];
  }
  return undefined;
}

function toDate(value) {
  if (!value) return null;
  return String(value);
}

function startOfToday() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

function parseDateOnly(value) {
  if (!value) return null;
  const raw = String(value).slice(0, 10);
  const d = new Date(`${raw}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function daysUntil(endDate) {
  const end = parseDateOnly(endDate);
  if (!end) return null;
  const today = startOfToday();
  return Math.round((end.getTime() - today.getTime()) / 86400000);
}

/**
 * End date is authoritative:
 * - Status Active + EndDate < today → expired (block)
 * - Status Active + EndDate within 10 days → warning
 */
function applyContractDateRules(portal) {
  if (!portal?.Found) return portal;

  const today = startOfToday();
  const contracts = (portal.Contracts || []).map((c) => {
    const status = String(c.Status || '').trim();
    const statusLower = status.toLowerCase();
    const cancelled = statusLower === 'cancelled';
    const end = parseDateOnly(c.EndDate);
    const endPassed = Boolean(end && end < today);
    const days = end ? daysUntil(c.EndDate) : null;
    const isExpired =
      !cancelled &&
      (endPassed || statusLower === 'expired' || Boolean(c.IsExpired));
    const displayStatus =
      isExpired && statusLower === 'active' ? 'Expired' : status;

    return {
      ...c,
      Status: displayStatus,
      DaysToExpiry: days != null ? days : c.DaysToExpiry || 0,
      IsExpired: isExpired,
    };
  });

  // Valid for access: Active AND EndDate >= today
  const hasActiveContract = contracts.some((c) => {
    const end = parseDateOnly(c.EndDate);
    if (!end || end < today) return false;
    return String(c.Status || '').toLowerCase() === 'active';
  });

  // Expired lock: no valid Active contract, and at least one EndDate has passed
  const hasExpiredContract =
    !hasActiveContract &&
    contracts.some((c) => {
      const end = parseDateOnly(c.EndDate);
      return Boolean(end && end < today);
    });

  let nearestContractExpiry = portal.NearestContractExpiry || '';
  let daysToContractExpiry = Number(portal.DaysToContractExpiry || 0);
  let contractExpiringSoon = false;

  if (hasActiveContract) {
    const valid = contracts
      .filter((c) => {
        const end = parseDateOnly(c.EndDate);
        return (
          end &&
          end >= today &&
          String(c.Status || '').toLowerCase() === 'active'
        );
      })
      .sort((a, b) => String(a.EndDate).localeCompare(String(b.EndDate)));
    const nearest = valid[0];
    if (nearest) {
      nearestContractExpiry = nearest.EndDate;
      daysToContractExpiry = daysUntil(nearest.EndDate) ?? 0;
      // 10-day warning before end-date lockout
      contractExpiringSoon =
        daysToContractExpiry >= 0 && daysToContractExpiry <= 10;
    }
  }

  const isAccountSuspended = Boolean(portal.IsAccountSuspended);
  const hasOverdue = Boolean(portal.HasOverduePayments);
  let blockReason = '';
  let isAccessBlocked = false;

  if (isAccountSuspended) {
    blockReason = 'Suspended';
    isAccessBlocked = true;
  } else if (hasOverdue) {
    blockReason = 'Overdue';
    isAccessBlocked = true;
  } else if (hasExpiredContract) {
    blockReason = 'ContractExpired';
    isAccessBlocked = true;
  }

  // Payment reminder: Pending due within 5 days (client-side fallback)
  const allPayments = [
    ...(portal.Payments || []),
    ...(portal.UpcomingPayments || []),
  ];
  const seenPay = new Set();
  const upcoming = [];
  for (const p of allPayments) {
    const id = p.Id || `${p.ContractId}-${p.FeeName}-${p.DueDate}`;
    if (seenPay.has(id)) continue;
    seenPay.add(id);
    const status = String(p.Status || '').toLowerCase();
    if (status !== 'pending') continue;
    const effective = p.EffectiveDueDate || p.ExtendedDueDate || p.DueDate;
    const days =
      p.DaysUntilDue != null && p.DaysUntilDue !== ''
        ? Number(p.DaysUntilDue)
        : daysUntil(effective);
    if (days == null || days < 0 || days > 5) continue;
    upcoming.push({
      ...p,
      EffectiveDueDate: effective,
      DaysUntilDue: days,
    });
  }
  upcoming.sort((a, b) => String(a.EffectiveDueDate).localeCompare(String(b.EffectiveDueDate)));

  const paymentDueSoon = upcoming.length > 0 && !isAccessBlocked;
  const upcomingPaymentCount = upcoming.length;
  const upcomingPaymentAmount = upcoming.reduce((s, p) => s + Number(p.Amount || 0), 0);
  const nearestPaymentDue = upcoming[0]?.EffectiveDueDate || portal.NearestPaymentDue || '';
  const daysToNearestPayment =
    upcoming[0] != null ? upcoming[0].DaysUntilDue : Number(portal.DaysToNearestPayment || 0);

  return {
    ...portal,
    Contracts: contracts,
    HasActiveContract: hasActiveContract,
    HasExpiredContract: hasExpiredContract,
    ContractExpiringSoon: contractExpiringSoon && !isAccessBlocked,
    DaysToContractExpiry: daysToContractExpiry,
    NearestContractExpiry: nearestContractExpiry,
    PaymentDueSoon: paymentDueSoon || (Boolean(portal.PaymentDueSoon) && !isAccessBlocked),
    UpcomingPayments: upcoming.length ? upcoming : portal.UpcomingPayments || [],
    UpcomingPaymentCount: upcomingPaymentCount || Number(portal.UpcomingPaymentCount || 0),
    UpcomingPaymentAmount: upcomingPaymentAmount || Number(portal.UpcomingPaymentAmount || 0),
    NearestPaymentDue: nearestPaymentDue,
    DaysToNearestPayment: daysToNearestPayment,
    BlockReason: blockReason || portal.BlockReason || '',
    IsAccessBlocked: isAccessBlocked,
  };
}

function normalizePortal(raw) {
  const o = raw || {};
  const contracts = Array.isArray(pick(o, 'Contracts', 'contracts'))
    ? pick(o, 'Contracts', 'contracts')
    : [];
  const licenses = Array.isArray(pick(o, 'Licenses', 'licenses'))
    ? pick(o, 'Licenses', 'licenses')
    : [];
  const payments = Array.isArray(pick(o, 'Payments', 'payments'))
    ? pick(o, 'Payments', 'payments')
    : [];
  const overdue = Array.isArray(pick(o, 'OverduePayments', 'overduePayments'))
    ? pick(o, 'OverduePayments', 'overduePayments')
    : [];

  const mapPayment = (p) => {
    const dueDate = toDate(pick(p, 'DueDate', 'dueDate'));
    const extended = toDate(pick(p, 'ExtendedDueDate', 'extendedDueDate'));
    const effectiveDue = extended || dueDate;
    const apiDays = pick(p, 'DaysUntilDue', 'daysUntilDue');
    const computedDays = daysUntil(effectiveDue);
    const daysUntilDue =
      apiDays != null && apiDays !== '' ? Number(apiDays) : computedDays != null ? computedDays : 0;
    return {
      Id: pick(p, 'Id', 'id') || 0,
      ContractId: pick(p, 'ContractId', 'contractId') || 0,
      ContractNo: pick(p, 'ContractNo', 'contractNo') || '',
      FeeName: pick(p, 'FeeName', 'feeName') || '',
      PaymentType: pick(p, 'PaymentType', 'paymentType') || '',
      Amount: Number(pick(p, 'Amount', 'amount') || 0),
      DueDate: dueDate,
      ExtendedDueDate: extended,
      EffectiveDueDate: toDate(pick(p, 'EffectiveDueDate', 'effectiveDueDate')) || effectiveDue,
      PaidDate: toDate(pick(p, 'PaidDate', 'paidDate')),
      Status: pick(p, 'Status', 'status') || '',
      InstallmentNo: pick(p, 'InstallmentNo', 'installmentNo') || 0,
      DaysOverdue: pick(p, 'DaysOverdue', 'daysOverdue') || 0,
      DaysUntilDue: daysUntilDue,
    };
  };

  const base = {
    Found: Boolean(pick(o, 'Found', 'found')),
    Status: pick(o, 'Status', 'status') || '',
    Message: pick(o, 'Message', 'message') || '',
    Code: pick(o, 'Code', 'code') || '',
    CustomerId: pick(o, 'CustomerId', 'customerId') || 0,
    CustomerName: pick(o, 'CustomerName', 'customerName') || '',
    MobileNo: pick(o, 'MobileNo', 'mobileNo') || '',
    EmailId: pick(o, 'EmailId', 'emailId') || '',
    HasOverduePayments: Boolean(pick(o, 'HasOverduePayments', 'hasOverduePayments')),
    IsAccountSuspended: Boolean(pick(o, 'IsAccountSuspended', 'isAccountSuspended')),
    SuspendReason: pick(o, 'SuspendReason', 'suspendReason') || '',
    IsAccessBlocked: Boolean(
      pick(o, 'IsAccessBlocked', 'isAccessBlocked') ||
        pick(o, 'IsAccountSuspended', 'isAccountSuspended') ||
        pick(o, 'HasOverduePayments', 'hasOverduePayments') ||
        pick(o, 'HasExpiredContract', 'hasExpiredContract'),
    ),
    BlockReason: pick(o, 'BlockReason', 'blockReason') || '',
    HasExpiredLicense: Boolean(pick(o, 'HasExpiredLicense', 'hasExpiredLicense')),
    HasActiveLicense: Boolean(pick(o, 'HasActiveLicense', 'hasActiveLicense')),
    HasExpiredContract: Boolean(pick(o, 'HasExpiredContract', 'hasExpiredContract')),
    HasActiveContract: Boolean(pick(o, 'HasActiveContract', 'hasActiveContract')),
    ContractExpiringSoon: Boolean(pick(o, 'ContractExpiringSoon', 'contractExpiringSoon')),
    DaysToContractExpiry: Number(pick(o, 'DaysToContractExpiry', 'daysToContractExpiry') || 0),
    NearestContractExpiry: toDate(pick(o, 'NearestContractExpiry', 'nearestContractExpiry')),
    OverdueAmount: Number(pick(o, 'OverdueAmount', 'overdueAmount') || 0),
    OverdueCount: Number(pick(o, 'OverdueCount', 'overdueCount') || 0),
    PaymentDueSoon: Boolean(pick(o, 'PaymentDueSoon', 'paymentDueSoon')),
    UpcomingPaymentCount: Number(pick(o, 'UpcomingPaymentCount', 'upcomingPaymentCount') || 0),
    UpcomingPaymentAmount: Number(pick(o, 'UpcomingPaymentAmount', 'upcomingPaymentAmount') || 0),
    DaysToNearestPayment: Number(pick(o, 'DaysToNearestPayment', 'daysToNearestPayment') || 0),
    NearestPaymentDue: toDate(pick(o, 'NearestPaymentDue', 'nearestPaymentDue')),
    NearestLicenseExpiry: toDate(pick(o, 'NearestLicenseExpiry', 'nearestLicenseExpiry')),
    Contracts: contracts.map((c) => ({
      Id: pick(c, 'Id', 'id') || 0,
      ContractNo: pick(c, 'ContractNo', 'contractNo') || '',
      Title: pick(c, 'Title', 'title') || '',
      ProductName: pick(c, 'ProductName', 'productName') || '',
      StartDate: toDate(pick(c, 'StartDate', 'startDate')),
      EndDate: toDate(pick(c, 'EndDate', 'endDate')),
      Status: pick(c, 'Status', 'status') || '',
      LicenseFeeType: pick(c, 'LicenseFeeType', 'licenseFeeType') || '',
      LicenseFeeAmount: Number(pick(c, 'LicenseFeeAmount', 'licenseFeeAmount') || 0),
      LicenseDurationMonths: pick(c, 'LicenseDurationMonths', 'licenseDurationMonths') || 0,
      Notes: pick(c, 'Notes', 'notes') || '',
      FilePath: pick(c, 'FilePath', 'filePath') || '',
      HasFile: Boolean(pick(c, 'HasFile', 'hasFile')) || Boolean(pick(c, 'FilePath', 'filePath')),
      DaysToExpiry: pick(c, 'DaysToExpiry', 'daysToExpiry') || 0,
      IsExpired: Boolean(pick(c, 'IsExpired', 'isExpired')),
    })),
    Licenses: licenses.map((l) => ({
      Id: pick(l, 'Id', 'id') || 0,
      ContractId: pick(l, 'ContractId', 'contractId') || 0,
      ContractNo: pick(l, 'ContractNo', 'contractNo') || '',
      LicenseKey: pick(l, 'LicenseKey', 'licenseKey') || '',
      ProductName: pick(l, 'ProductName', 'productName') || '',
      IssueDate: toDate(pick(l, 'IssueDate', 'issueDate')),
      ExpiryDate: toDate(pick(l, 'ExpiryDate', 'expiryDate')),
      Status: pick(l, 'Status', 'status') || '',
      MaxUsers: pick(l, 'MaxUsers', 'maxUsers') || 0,
      MaxDevices: pick(l, 'MaxDevices', 'maxDevices') || 0,
      ApiEnabled: Boolean(pick(l, 'ApiEnabled', 'apiEnabled')),
      DaysToExpiry: pick(l, 'DaysToExpiry', 'daysToExpiry') || 0,
      IsExpired: Boolean(pick(l, 'IsExpired', 'isExpired')),
    })),
    Payments: payments.map(mapPayment),
    OverduePayments: overdue.map(mapPayment),
    UpcomingPayments: (
      Array.isArray(pick(o, 'UpcomingPayments', 'upcomingPayments'))
        ? pick(o, 'UpcomingPayments', 'upcomingPayments')
        : []
    ).map(mapPayment),
  };

  return applyContractDateRules(base);
}

/** True when product access should be locked. SA still bypasses in UI. */
export function isPortalAccessBlocked(portal) {
  if (!portal?.Found) return false;
  const gated = applyContractDateRules(portal);
  if (gated.IsAccessBlocked) return true;
  return Boolean(
    gated.IsAccountSuspended ||
      gated.HasOverduePayments ||
      gated.HasExpiredContract,
  );
}

export function getPortalBlockReason(portal) {
  if (!portal?.Found) return '';
  const gated = applyContractDateRules(portal);
  if (gated.BlockReason) return gated.BlockReason;
  if (gated.IsAccountSuspended) return 'Suspended';
  if (gated.HasOverduePayments) return 'Overdue';
  if (gated.HasExpiredContract) return 'ContractExpired';
  return '';
}

const PORTAL_CACHE_KEY = 'ainsoft.managementPortal';
const SUSPENDED_KEY = 'ainsoft.accountSuspended';
const LICENSE_WARN_DISMISSED_KEY = 'ainsoft.licenseWarnDismissed';
const CONTRACT_WARN_DISMISSED_KEY = 'ainsoft.contractWarnDismissed';
const PAYMENT_WARN_DISMISSED_KEY = 'ainsoft.paymentWarnDismissed';

export function cachePortal(portal) {
  try {
    localStorage.setItem(PORTAL_CACHE_KEY, JSON.stringify(portal));
  } catch {
    /* ignore */
  }
}

export function readCachedPortal() {
  try {
    const raw = localStorage.getItem(PORTAL_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearManagementCache() {
  try {
    localStorage.removeItem(PORTAL_CACHE_KEY);
    localStorage.removeItem(SUSPENDED_KEY);
    sessionStorage.removeItem(LICENSE_WARN_DISMISSED_KEY);
    sessionStorage.removeItem(CONTRACT_WARN_DISMISSED_KEY);
    sessionStorage.removeItem(PAYMENT_WARN_DISMISSED_KEY);
  } catch {
    /* ignore */
  }
}

export function setAccountSuspended(flag) {
  try {
    if (flag) localStorage.setItem(SUSPENDED_KEY, '1');
    else localStorage.removeItem(SUSPENDED_KEY);
  } catch {
    /* ignore */
  }
}

export function isAccountSuspended() {
  try {
    return localStorage.getItem(SUSPENDED_KEY) === '1';
  } catch {
    return false;
  }
}

export function isLicenseWarnDismissed() {
  try {
    return sessionStorage.getItem(LICENSE_WARN_DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

export function dismissLicenseWarn() {
  try {
    sessionStorage.setItem(LICENSE_WARN_DISMISSED_KEY, '1');
  } catch {
    /* ignore */
  }
}

export function isContractWarnDismissed() {
  try {
    return sessionStorage.getItem(CONTRACT_WARN_DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

export function dismissContractWarn() {
  try {
    sessionStorage.setItem(CONTRACT_WARN_DISMISSED_KEY, '1');
  } catch {
    /* ignore */
  }
}

export function isPaymentWarnDismissed() {
  try {
    return sessionStorage.getItem(PAYMENT_WARN_DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

export function dismissPaymentWarn() {
  try {
    sessionStorage.setItem(PAYMENT_WARN_DISMISSED_KEY, '1');
  } catch {
    /* ignore */
  }
}

export function isSaRole(role) {
  return String(role || '').toUpperCase() === 'SA';
}

/** Admin sidebar tab — SA and CA only. */
export function isAdminTabRole(role) {
  const upper = String(role || '').toUpperCase();
  return upper === 'SA' || upper === 'CA';
}

/** Roles allowed to open License Info in the delivery app. */
export function canViewLicenseInfo(role) {
  const r = String(role ?? '').trim();
  if (r === '0') return true;
  const upper = r.toUpperCase();
  return upper === 'ADMIN' || upper === 'SA' || upper === 'CA';
}

/**
 * Resolve RegisterCode from company profile object (various casings).
 */
export function getRegisterCodeFromProfile(profile) {
  if (!profile || typeof profile !== 'object') return '';
  return String(
    profile.RegisterCode ||
      profile.registerCode ||
      profile.Register_Code ||
      '',
  ).trim();
}
